import { BigNumber } from "bignumber.js";
import { z } from "zod";
import { ESTIMATION_TYPE } from "~/models/DealConstruction";
import type { DealEstimationOcrRequestItem } from "~/models/DealEstimationOcrRequest";
import { removeNewlines } from "~/utils/StringUtil";

export const DEAL_ESTIMATION_OCR_MAX_NOTE_LENGTH = 400;
export const DEAL_ESTIMATION_OCR_MIN_CHARGE_PER = 0;
export const DEAL_ESTIMATION_OCR_MAX_CHARGE_PER = 100;
export const DEAL_ESTIMATION_OCR_TOTAL_CHARGE_PER = 100;
export const DEAL_ESTIMATION_OCR_MIN_UNIT = 1;
export const DEAL_ESTIMATION_OCR_TOTAL_CHARGE_PER_ERROR_MESSAGE =
	"合計が100になるように入力してください";

const nullableShortTextSchema = z
	.string()
	.transform((value) => {
		const trimmedValue = value.trim();
		return trimmedValue === "" ? null : trimmedValue;
	})
	.pipe(z.string().max(255, "255文字以内で入力してください").nullable());

const requiredShortTextSchema = z
	.string()
	.transform((value) => value.trim())
	.pipe(
		z
			.string()
			.min(1, "入力してください")
			.max(255, "255文字以内で入力してください")
	);

const requiredNumberSchema = (numberSchema: z.ZodNumber) =>
	z
		.number({ error: "入力してください" })
		.nullable()
		.pipe(numberSchema.nullable())
		.refine((value): boolean => value !== null, "入力してください");

const chargePerSchema = requiredNumberSchema(
	z
		.number()
		.int("整数で入力してください")
		.min(DEAL_ESTIMATION_OCR_MIN_CHARGE_PER, "0以上で設定してください")
		.max(DEAL_ESTIMATION_OCR_MAX_CHARGE_PER, "100以下で設定してください")
);

export const dealEstimationOcrFormItemSchema = z
	.object({
		companyChargePer: chargePerSchema,
		estimationType: z.enum(Object.values(ESTIMATION_TYPE)),
		note: z
			.string()
			.transform((value) => (value === "" ? null : value))
			.refine(
				(value) =>
					value === null ||
					removeNewlines(value).length <= DEAL_ESTIMATION_OCR_MAX_NOTE_LENGTH,
				`${DEAL_ESTIMATION_OCR_MAX_NOTE_LENGTH}文字以内で入力してください`
			),
		otherName: requiredShortTextSchema,
		ownerChargePer: chargePerSchema,
		point: nullableShortTextSchema,
		renteeChargePer: chargePerSchema,
		unit: requiredNumberSchema(
			z.number().min(DEAL_ESTIMATION_OCR_MIN_UNIT, "1以上で設定してください")
		),
		unitAmount: requiredNumberSchema(z.number().int("整数で入力してください")),
		unitName: requiredShortTextSchema
	})
	.superRefine((item, context) => {
		if (
			item.renteeChargePer !== null &&
			item.ownerChargePer !== null &&
			item.companyChargePer !== null &&
			item.renteeChargePer + item.ownerChargePer + item.companyChargePer !==
				DEAL_ESTIMATION_OCR_TOTAL_CHARGE_PER
		) {
			// メッセージが3つ並ぶと冗長なため、代表して借主負担%にのみ設定する
			context.addIssue({
				code: "custom",
				message: DEAL_ESTIMATION_OCR_TOTAL_CHARGE_PER_ERROR_MESSAGE,
				path: ["renteeChargePer"]
			});
		}
	});

export const dealEstimationOcrFormSchema = z.object({
	items: z.array(dealEstimationOcrFormItemSchema)
});

export type DealEstimationOcrFormItem = z.input<
	typeof dealEstimationOcrFormItemSchema
>;

export type DealEstimationOcrFormInput = z.input<
	typeof dealEstimationOcrFormSchema
>;

export type DealEstimationOcrFormValues = z.output<
	typeof dealEstimationOcrFormSchema
>;

export type DealEstimationOcrAmounts = {
	nonProposalTotalAmount: number;
	proposalTotalAmount: number;
	totalAmount: number;
};

type ChargePers = Pick<
	DealEstimationOcrRequestItem,
	"companyChargePer" | "ownerChargePer" | "renteeChargePer"
>;

const CHARGE_PER_KEYS = [
	"companyChargePer",
	"ownerChargePer",
	"renteeChargePer"
] as const satisfies readonly (keyof ChargePers)[];

/**
 * 負担割合のうち1つだけが読み取れなかった場合に、合計が100になるように補完する。
 * 補完値が0〜100の範囲外になる場合は、読み取り結果自体が誤っているため補完しない。
 */
const complementChargePers = (chargePers: ChargePers): ChargePers => {
	const missingKeys = CHARGE_PER_KEYS.filter((key) => chargePers[key] === null);
	if (missingKeys.length !== 1) {
		return chargePers;
	}

	const [missingKey] = missingKeys;
	const filledTotal = CHARGE_PER_KEYS.filter(
		(key) => key !== missingKey
	).reduce((total, key) => total + (chargePers[key] ?? 0), 0);
	const complemented = DEAL_ESTIMATION_OCR_TOTAL_CHARGE_PER - filledTotal;
	if (
		complemented < DEAL_ESTIMATION_OCR_MIN_CHARGE_PER ||
		complemented > DEAL_ESTIMATION_OCR_MAX_CHARGE_PER
	) {
		return chargePers;
	}

	return { ...chargePers, [missingKey]: complemented };
};

export const buildDealEstimationOcrFormValues = (
	items: DealEstimationOcrRequestItem[]
): DealEstimationOcrFormInput => ({
	items: items.map((item) => {
		const complementedChargePers = complementChargePers({
			companyChargePer: item.companyChargePer,
			ownerChargePer: item.ownerChargePer,
			renteeChargePer: item.renteeChargePer
		});
		return {
			companyChargePer: complementedChargePers.companyChargePer,
			estimationType: ESTIMATION_TYPE.NONE,
			note: item.note ?? "",
			otherName: item.otherName,
			ownerChargePer: complementedChargePers.ownerChargePer,
			point: item.point ?? "",
			renteeChargePer: complementedChargePers.renteeChargePer,
			unit: item.unit,
			unitAmount: item.unitAmount,
			unitName: item.unitName ?? ""
		};
	})
});

export const buildEmptyDealEstimationOcrFormItem =
	(): DealEstimationOcrFormItem => ({
		companyChargePer: null,
		estimationType: ESTIMATION_TYPE.NONE,
		note: "",
		otherName: "",
		ownerChargePer: null,
		point: "",
		renteeChargePer: null,
		unit: null,
		unitAmount: null,
		unitName: ""
	});

export const calculateDealEstimationOcrItemAmount = (
	item: Pick<DealEstimationOcrFormItem, "unit" | "unitAmount">
): number | null => {
	if (
		item.unit === null ||
		item.unitAmount === null ||
		!Number.isFinite(item.unit) ||
		!Number.isFinite(item.unitAmount)
	) {
		return null;
	}

	return BigNumber(item.unit).multipliedBy(item.unitAmount).toNumber();
};

export const calculateDealEstimationOcrAmounts = (
	items: DealEstimationOcrFormItem[]
): DealEstimationOcrAmounts => {
	const { nonProposalTotalAmount, proposalTotalAmount } = items.reduce(
		(acc, item) => {
			const amount = calculateDealEstimationOcrItemAmount(item) ?? 0;
			if (item.estimationType === ESTIMATION_TYPE.PROPOSAL) {
				return {
					...acc,
					proposalTotalAmount: BigNumber(acc.proposalTotalAmount)
						.plus(amount)
						.toNumber()
				};
			}
			return {
				...acc,
				nonProposalTotalAmount: BigNumber(acc.nonProposalTotalAmount)
					.plus(amount)
					.toNumber()
			};
		},
		{ nonProposalTotalAmount: 0, proposalTotalAmount: 0 }
	);

	return {
		nonProposalTotalAmount,
		proposalTotalAmount,
		totalAmount: BigNumber(nonProposalTotalAmount)
			.plus(proposalTotalAmount)
			.toNumber()
	};
};
